import { Injectable } from '@angular/core';
import { Geolocation } from '@capacitor/geolocation';
import { PlatformDetectionService } from './platform-detection.service';

export interface LocationPermissionResult {
  location: 'granted' | 'denied' | 'prompt';
}

export interface LocationPosition {
  coords: {
    latitude: number;
    longitude: number;
    accuracy: number;
    altitude?: number;
    altitudeAccuracy?: number;
    heading?: number;
    speed?: number;
  };
  timestamp: number;
}

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  constructor(private platformDetection: PlatformDetectionService) {}

  async checkPermissions(): Promise<LocationPermissionResult> {
    if (this.platformDetection.isWeb()) {
      // For web, we check if geolocation is available and try to get permission status
      if (!navigator.geolocation) {
        return { location: 'denied' };
      }

      // Check if permissions API is available (modern browsers)
      if ('permissions' in navigator) {
        try {
          const permission = await navigator.permissions.query({
            name: 'geolocation',
          });
          switch (permission.state) {
            case 'granted':
              return { location: 'granted' };
            case 'denied':
              return { location: 'denied' };
            case 'prompt':
            default:
              return { location: 'prompt' };
          }
        } catch (error) {
          // Fallback if permissions API fails
          return { location: 'prompt' };
        }
      } else {
        // For older browsers, we assume prompt until we try to get location
        return { location: 'prompt' };
      }
    } else {
      // Native platform - use Capacitor Geolocation
      const result = await Geolocation.checkPermissions();
      return {
        location: result.location as 'granted' | 'denied' | 'prompt',
      };
    }
  }

  async requestPermissions(): Promise<LocationPermissionResult> {
    if (this.platformDetection.isWeb()) {
      // For web, requesting permission is done by attempting to get location
      try {
        await this.getCurrentPosition({ timeout: 1000 });
        return { location: 'granted' };
      } catch (error) {
        console.error('Web location permission request failed:', error);
        if (error.code === 1) {
          // PERMISSION_DENIED
          return { location: 'denied' };
        }
        return { location: 'prompt' };
      }
    } else {
      // Native platform - use Capacitor Geolocation
      const result = await Geolocation.requestPermissions();
      return {
        location: result.location as 'granted' | 'denied' | 'prompt',
      };
    }
  }

  async getCurrentPosition(options?: {
    timeout?: number;
    enableHighAccuracy?: boolean;
    maximumAge?: number;
  }): Promise<LocationPosition> {
    if (this.platformDetection.isWeb()) {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by this browser'));
          return;
        }

        const defaultOptions = {
          timeout: 30000,
          enableHighAccuracy: true,
          maximumAge: 0,
          ...options,
        };

        navigator.geolocation.getCurrentPosition(
          (position) => {
            resolve({
              coords: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude || undefined,
                altitudeAccuracy: position.coords.altitudeAccuracy || undefined,
                heading: position.coords.heading || undefined,
                speed: position.coords.speed || undefined,
              },
              timestamp: position.timestamp,
            });
          },
          (error) => {
            reject(error);
          },
          defaultOptions
        );
      });
    } else {
      // Native platform - use Capacitor Geolocation
      const position = await Geolocation.getCurrentPosition({
        timeout: options?.timeout || 30000,
        enableHighAccuracy: options?.enableHighAccuracy !== false,
      });

      return {
        coords: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          altitude: position.coords.altitude,
          altitudeAccuracy: position.coords.altitudeAccuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
        },
        timestamp: position.timestamp,
      };
    }
  }

  async watchPosition(
    options: {
      timeout?: number;
      enableHighAccuracy?: boolean;
      maximumAge?: number;
    },
    callback: (position: LocationPosition) => void,
    errorCallback: (error: any) => void
  ): Promise<string> {
    if (this.platformDetection.isWeb()) {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by this browser'));
          return;
        }

        const defaultOptions = {
          timeout: 30000,
          enableHighAccuracy: true,
          maximumAge: 0,
          ...options,
        };

        const watchId = navigator.geolocation.watchPosition(
          (position) => {
            callback({
              coords: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude || undefined,
                altitudeAccuracy: position.coords.altitudeAccuracy || undefined,
                heading: position.coords.heading || undefined,
                speed: position.coords.speed || undefined,
              },
              timestamp: position.timestamp,
            });
          },
          errorCallback,
          defaultOptions
        );

        resolve(watchId.toString());
      });
    } else {
      // Native platform - use Capacitor Geolocation
      const watchId = await Geolocation.watchPosition(
        {
          timeout: options?.timeout || 30000,
          enableHighAccuracy: options?.enableHighAccuracy !== false,
        },
        (position, error) => {
          if (error) {
            errorCallback(error);
          } else if (position) {
            callback({
              coords: {
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                accuracy: position.coords.accuracy,
                altitude: position.coords.altitude,
                altitudeAccuracy: position.coords.altitudeAccuracy,
                heading: position.coords.heading,
                speed: position.coords.speed,
              },
              timestamp: position.timestamp,
            });
          }
        }
      );

      return watchId;
    }
  }

  async clearWatch(watchId: string): Promise<void> {
    if (this.platformDetection.isWeb()) {
      navigator.geolocation.clearWatch(parseInt(watchId));
    } else {
      await Geolocation.clearWatch({ id: watchId });
    }
  }
}
